import { useEffect } from 'react';
import io from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/**
 * Hook para sincronizar ofertas en tiempo real usando Socket.io
 * Reemplaza el polling cada 30 segundos con eventos en tiempo real
 *
 * @param {Function} onOffersUpdate - Callback que recibe (update, action)
 *   - action='initial': Carga inicial de ofertas
 *   - action='create': Nueva oferta creada
 *   - action='delete': Oferta deactivada
 *
 * @example
 * const handleOfferUpdate = useCallback((update, action) => {
 *   if (action === 'initial') setApiOffers(update.map(normalizeApiOffer));
 *   else if (action === 'create') setApiOffers(prev => [normalize(update[0]), ...prev]);
 *   else if (action === 'delete') setApiOffers(prev => prev.filter(o => o.id !== update.id));
 * }, []);
 * useOfferSubscription(handleOfferUpdate);
 */
export function useOfferSubscription(onOffersUpdate) {
  useEffect(() => {
    if (!onOffersUpdate) return;

    let socket = null;
    const maxReconnectAttempts = 5;

    const connectSocket = () => {
      try {
        socket = io(API_URL, {
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          reconnectionAttempts: maxReconnectAttempts,
          transports: ['websocket', 'polling'],
        });

        socket.on('connect', () => {
          console.log('🔌 Marketplace WebSocket conectado:', socket.id);
          socket.emit('join_marketplace');

          // Fetch inicial de ofertas
          fetch(`${API_URL}/api/offers`)
            .then(r => r.json())
            .then(data => {
              console.log(`📊 Ofertas iniciales cargadas: ${data.length}`);
              onOffersUpdate(data, 'initial');
            })
            .catch(err => console.error('Error al cargar ofertas iniciales:', err));
        });

        // Nueva oferta creada
        socket.on('offer_created', (newOffer) => {
          console.log('✨ Nueva oferta:', newOffer.id);
          // Add timestamp for real-time indicator
          const enrichedOffer = { ...newOffer, _createdAt: new Date().toISOString() };
          onOffersUpdate([enrichedOffer], 'create');
        });

        // Oferta actualizada
        socket.on('offer_updated', (updatedOffer) => {
          console.log('🔄 Oferta actualizada:', updatedOffer.id);
          onOffersUpdate([updatedOffer], 'update');
        });

        // Oferta deactivada
        socket.on('offer_deactivated', (offerId) => {
          console.log('❌ Oferta deactivada:', offerId);
          onOffersUpdate({ id: offerId, removed: true }, 'delete');
        });

        socket.on('disconnect', () => {
          console.log('📡 Marketplace WebSocket desconectado');
        });

        socket.on('connect_error', (err) => {
          console.warn('⚠️ WebSocket error:', err.message);
        });
      } catch (err) {
        console.error('Error al conectar WebSocket:', err);
      }
    };

    connectSocket();

    return () => {
      if (socket) {
        socket.emit('leave_marketplace');
        socket.disconnect();
      }
    };
  }, [onOffersUpdate]);
}
